import { Component, ErrorInfo, ReactNode } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Container,
  Heading,
  Text,
  VStack,
  Code,
} from '@chakra-ui/react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Store error info for display
    this.setState({ errorInfo });

    // Log to console for development
    console.error('Uncaught error:', error, errorInfo);

    // Report to error tracking service (if configured)
    this.reportError(error, errorInfo);
  }

  private reportError(error: Error, errorInfo: ErrorInfo) {
    // Send to error tracking service if available
    // This integrates with services like Sentry, LogRocket, etc.
    if (typeof window !== 'undefined' && (window as any).Sentry) {
      (window as any).Sentry.captureException(error, {
        extra: {
          componentStack: errorInfo.componentStack,
        },
      });
    }

    // Also log to any custom error endpoint
    try {
      const errorReport = {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        url: window.location.href,
        timestamp: new Date().toISOString(),
      };
      // Log for debugging - can be replaced with actual API call
      console.info('Error report:', errorReport);
    } catch (reportingError) {
      console.error('Failed to report error:', reportingError);
    }
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  public render() {
    if (this.state.hasError) {
      return (
        <Container maxW="container.md" py={10}>
          <VStack spacing={6} align="stretch">
            <Heading 
              size="xl" 
              bgGradient="linear(to-r, red.400, pink.400)"
              bgClip="text"
            >
              Something went wrong
            </Heading>
            
            <Text fontSize="lg" color="gray.400">
              We apologize for the inconvenience. Please try refreshing the page or contact support if the problem persists.
            </Text>

            {this.state.error && (
              <Box 
                bg="whiteAlpha.100" 
                p={4} 
                borderRadius="md"
                borderWidth="1px"
                borderColor="whiteAlpha.200"
              >
                <Code 
                  bg="transparent" 
                  color="red.300"
                >
                  {this.state.error.message}
                </Code>
              </Box>
            )}

            <ButtonGroup spacing={4}>
              <Button
                colorScheme="purple"
                onClick={this.handleReset}
                size="lg"
                variant="outline"
              >
                Try Again
              </Button>
              <Button
                colorScheme="purple"
                onClick={() => window.location.reload()}
                size="lg"
              >
                Refresh Page
              </Button>
            </ButtonGroup>
          </VStack>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;